import { showToast } from '@/components/ui/toast';
import { config, isProduction } from '@/config/environment';

// ============================================================================
// Types
// ============================================================================

export interface ErrorReport {
  errorId: string;
  message: string;
  name: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  userId?: string;
  sessionId?: string;
  retryCount: number;
  context?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  tags?: string[];
  fingerprint?: string[];
}

export interface ErrorReportingConfig {
  provider: 'console' | 'sentry' | 'custom' | 'multiple';
  apiEndpoint?: string;
  apiKey?: string;
  projectId?: string;
  environment?: string;
  enableUserFeedback?: boolean;
  enableScreenshots?: boolean;
  enablePerformanceMonitoring?: boolean;
  sampleRate?: number;
  beforeSend?: (errorReport: ErrorReport) => ErrorReport | null;
}

export interface ErrorContext {
  component?: string;
  action?: string;
  props?: Record<string, any>;
  state?: Record<string, any>;
  breadcrumbs?: Array<{
    timestamp: string;
    category: string;
    message: string;
    data?: Record<string, any>;
  }>;
}

// ============================================================================
// Error Categorization and Fingerprinting
// ============================================================================

export const categorizeErrorSeverity = (
  error: Error
): ErrorReport['severity'] => {
  const message = error.message.toLowerCase();

  // Critical errors that break core functionality
  if (
    message.includes('chunk load failed') ||
    message.includes('script error') ||
    message.includes('security') ||
    message.includes('cors')
  ) {
    return 'critical';
  }

  // High severity errors
  if (
    message.includes('network') ||
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('timeout')
  ) {
    return 'high';
  }

  // Medium severity errors
  if (
    message.includes('validation') ||
    message.includes('parse') ||
    message.includes('data')
  ) {
    return 'medium';
  }

  // Default to medium
  return 'medium';
};

export const generateFingerprint = (
  error: Error,
  context?: ErrorContext
): string[] => {
  const fingerprint = [error.name];

  // Add error message (normalized)
  const normalizedMessage = error.message
    .replace(/\d+/g, '[number]')
    .replace(/[a-f0-9-]{8,}/gi, '[id]')
    .replace(/https?:\/\/[^\s]+/gi, '[url]');

  fingerprint.push(normalizedMessage);

  // Add component context if available
  if (context?.component) {
    fingerprint.push(context.component);
  }

  // Add action context if available
  if (context?.action) {
    fingerprint.push(context.action);
  }

  return fingerprint;
};

// ============================================================================
// Error Reporting Service
// ============================================================================

class ErrorReportingService {
  private config: ErrorReportingConfig;
  private sessionId: string;
  private breadcrumbs: Array<{
    timestamp: string;
    category: string;
    message: string;
    data?: Record<string, any>;
  }> = [];

  constructor(config: ErrorReportingConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();
    this.initializeBreadcrumbCapture();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeBreadcrumbCapture() {
    // Capture navigation breadcrumbs
    if (typeof window !== 'undefined') {
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = (...args) => {
        this.addBreadcrumb('navigation', `Navigation to ${args[2]}`, {
          method: 'pushState',
        });
        return originalPushState.apply(window.history, args);
      };

      window.history.replaceState = (...args) => {
        this.addBreadcrumb('navigation', `Navigation to ${args[2]}`, {
          method: 'replaceState',
        });
        return originalReplaceState.apply(window.history, args);
      };

      // Capture click events on important elements
      document.addEventListener(
        'click',
        event => {
          const target = event.target as Element;
          if (
            target.tagName === 'BUTTON' ||
            target.tagName === 'A' ||
            target.getAttribute('role') === 'button'
          ) {
            this.addBreadcrumb(
              'ui',
              `Clicked ${target.tagName.toLowerCase()}`,
              {
                text: target.textContent?.trim().substring(0, 100),
                id: target.id,
                className: target.className,
              }
            );
          }
        },
        true
      );
    }
  }

  public addBreadcrumb(
    category: string,
    message: string,
    data?: Record<string, any>
  ) {
    this.breadcrumbs.push({
      timestamp: new Date().toISOString(),
      category,
      message,
      data,
    });

    // Keep only the last 50 breadcrumbs
    if (this.breadcrumbs.length > 50) {
      this.breadcrumbs = this.breadcrumbs.slice(-50);
    }
  }

  public async reportError(
    error: Error,
    errorInfo?: React.ErrorInfo,
    context?: ErrorContext
  ): Promise<boolean> {
    try {
      const errorReport: ErrorReport = {
        errorId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        message: error.message,
        name: error.name,
        stack: error.stack,
        componentStack: errorInfo?.componentStack || undefined,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.getCurrentUserId(),
        sessionId: this.sessionId,
        retryCount: 0,
        context: {
          ...context,
          breadcrumbs: this.breadcrumbs.slice(-20), // Include last 20 breadcrumbs
        },
        severity: categorizeErrorSeverity(error),
        tags: this.generateTags(error, context),
        fingerprint: generateFingerprint(error, context),
      };

      // Apply beforeSend filter
      const processedReport =
        this.config.beforeSend?.(errorReport) ?? errorReport;
      if (!processedReport) {
        return true; // Report was filtered out
      }

      // Send to configured provider(s)
      return await this.sendToProvider(processedReport);
    } catch (reportingError) {
      return false;
    }
  }

  private getCurrentUserId(): string | undefined {
    try {
      // Try to get user ID from localStorage, context, or other auth mechanism
      const authData = localStorage.getItem(config.auth.tokenKey);
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.userId || parsed.id || 'authenticated';
      }
      return 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  private generateTags(error: Error, context?: ErrorContext): string[] {
    const tags: string[] = [];

    // Add environment tag
    tags.push(`env:${this.config.environment || 'unknown'}`);

    // Add browser tag
    if (typeof window !== 'undefined') {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('chrome')) tags.push('browser:chrome');
      else if (userAgent.includes('firefox')) tags.push('browser:firefox');
      else if (userAgent.includes('safari')) tags.push('browser:safari');
      else if (userAgent.includes('edge')) tags.push('browser:edge');
      else tags.push('browser:unknown');
    }

    // Add component tag
    if (context?.component) {
      tags.push(`component:${context.component}`);
    }

    // Add action tag
    if (context?.action) {
      tags.push(`action:${context.action}`);
    }

    // Add error type tag
    tags.push(`error_type:${error.name}`);

    return tags;
  }

  private async sendToProvider(errorReport: ErrorReport): Promise<boolean> {
    switch (this.config.provider) {
      case 'console':
        return this.sendToConsole(errorReport);

      case 'sentry':
        return await this.sendToSentry(errorReport);

      case 'custom':
        return await this.sendToCustomEndpoint(errorReport);

      case 'multiple': {
        // Send to multiple providers
        const results = await Promise.allSettled([
          this.sendToConsole(errorReport),
          this.sendToSentry(errorReport),
          this.sendToCustomEndpoint(errorReport),
        ]);

        // Return true if at least one succeeded
        return results.some(
          result => result.status === 'fulfilled' && result.value
        );
      }

      default:
        return this.sendToConsole(errorReport);
    }
  }

  private sendToConsole(errorReport: ErrorReport): boolean {
    // In production, errors would be sent to external service
    // For development, could show in console if needed
    console.error(errorReport);
    return true;
  }

  private async sendToSentry(_errorReport: ErrorReport): Promise<boolean> {
    // Template method for Sentry integration
    // In production, this would integrate with Sentry SDK
    return true;
  }

  private async sendToCustomEndpoint(
    errorReport: ErrorReport
  ): Promise<boolean> {
    try {
      if (!this.config.apiEndpoint) {
        return false;
      }

      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && {
            Authorization: `Bearer ${this.config.apiKey}`,
          }),
        },
        body: JSON.stringify(errorReport),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  public async reportUserFeedback(
    errorId: string,
    feedback: {
      name?: string;
      email?: string;
      comments: string;
    }
  ): Promise<boolean> {
    try {
      this.addBreadcrumb('feedback', 'User provided feedback', {
        errorId,
        hasComments: !!feedback.comments,
      });

      // In a real implementation, this would be sent to your feedback endpoint

      showToast('Thank you for your feedback!', true, {
        position: 'top-center',
      });

      return true;
    } catch (error) {
      return false;
    }
  }
}

// ============================================================================
// Global Error Reporting Instance
// ============================================================================

let errorReportingInstance: ErrorReportingService | null = null;

export const initializeErrorReporting = (
  config: ErrorReportingConfig
): ErrorReportingService => {
  errorReportingInstance = new ErrorReportingService(config);

  // Set up global error handlers
  if (typeof window !== 'undefined') {
    window.addEventListener('error', event => {
      errorReportingInstance?.reportError(new Error(event.message), undefined, {
        component: 'Global',
        action: 'window.onerror',
      });
    });

    window.addEventListener('unhandledrejection', event => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      errorReportingInstance?.reportError(error, undefined, {
        component: 'Global',
        action: 'unhandledPromiseRejection',
      });
    });
  }

  return errorReportingInstance;
};

export const getErrorReporting = (): ErrorReportingService | null => {
  return errorReportingInstance;
};

// ============================================================================
// Default Configuration
// ============================================================================

export const createDefaultErrorReportingConfig = (): ErrorReportingConfig => ({
  provider: isProduction() ? 'multiple' : 'console',
  environment: isProduction() ? 'production' : 'development',
  enableUserFeedback: true,
  enableScreenshots: false,
  enablePerformanceMonitoring: isProduction(),
  sampleRate: isProduction() ? 0.1 : 1.0, // Sample 10% of errors in production
  beforeSend: errorReport => {
    // Filter out certain errors
    if (
      errorReport.message.includes('Non-Error promise rejection captured') ||
      errorReport.message.includes('Script error') ||
      errorReport.message.includes('Network request failed')
    ) {
      return null; // Don't report these errors
    }

    return errorReport;
  },
});

export default ErrorReportingService;
